import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';

interface TokenResponse {
  accessToken: string | null;
  idToken: string | null;
  expiresAt: number | null;
}

interface RefreshResult {
  success: boolean;
  tokens: TokenResponse | null;
  error: Error | null;
}

let refreshPromise: Promise<RefreshResult> | null = null;

const TOKEN_CACHE_KEY = 'beango_access_token';
const TOKEN_EXPIRY_KEY = 'beango_token_expires_at';

// ── Organization cache helpers ──────────────────────────────────────────

export interface CachedOrganization {
  id: number;
  uuid: string;
  name: string;
  slug: string;
  currency: string;
  timezone: string;
  settings: string;
  is_active: boolean;
  created_at: string;
}

export function getOrgCacheKey(userEmail: string): string {
  return `beango_orgs_${userEmail}`;
}

export function getCurrentOrgKey(userEmail: string): string {
  return `beango_current_org_${userEmail}`;
}

export function readOrgCache(
  userEmail: string
): { organizations: CachedOrganization[]; currentOrgId: number | null } | null {
  try {
    const orgsJson = localStorage.getItem(getOrgCacheKey(userEmail));
    if (!orgsJson) return null;
    const organizations = JSON.parse(orgsJson) as CachedOrganization[];
    const currentOrgIdRaw = localStorage.getItem(getCurrentOrgKey(userEmail));
    return {
      organizations,
      currentOrgId: currentOrgIdRaw ? Number(currentOrgIdRaw) : null,
    };
  } catch {
    return null;
  }
}

export function writeOrgCache(
  userEmail: string,
  organizations: CachedOrganization[],
  currentOrgId: number | null
): void {
  try {
    localStorage.setItem(getOrgCacheKey(userEmail), JSON.stringify(organizations));
    if (currentOrgId !== null) {
      localStorage.setItem(getCurrentOrgKey(userEmail), String(currentOrgId));
    } else {
      localStorage.removeItem(getCurrentOrgKey(userEmail));
    }
  } catch {
    // Ignore localStorage errors (e.g., quota exceeded)
  }
}

export function clearOrgCache(userEmail?: string): void {
  if (userEmail) {
    localStorage.removeItem(getOrgCacheKey(userEmail));
    localStorage.removeItem(getCurrentOrgKey(userEmail));
  } else {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('beango_orgs_') || key.startsWith('beango_current_org_'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }
}

export function getCachedToken(): string | null {
  const token = localStorage.getItem(TOKEN_CACHE_KEY);
  const expiresAt = localStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!token || !expiresAt) return null;

  if (Date.now() >= parseInt(expiresAt, 10) - 5 * 60 * 1000) {
    // Expired (with 5-min buffer) — clear cache
    localStorage.removeItem(TOKEN_CACHE_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    return null;
  }

  return token;
}

export function setCachedToken(token: string, expiresAt: number): void {
  localStorage.setItem(TOKEN_CACHE_KEY, token);
  localStorage.setItem(TOKEN_EXPIRY_KEY, String(expiresAt));
}

export function clearTokenCache(): void {
  localStorage.removeItem(TOKEN_CACHE_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  clearOrgCache();
}

export async function getValidAccessToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString() ?? null;
  } catch {
    return null;
  }
}

export async function getValidIdToken(): Promise<string | null> {
  // Read ID token directly from localStorage first — Amplify v6 stores session
  // data in IndexedDB which Playwright storageState() does NOT preserve.
  // Cognito raw tokens in localStorage survive state restores.
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.includes('CognitoIdentityServiceProvider') && key.includes('idToken')) {
      const token = localStorage.getItem(key);
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1])) as { exp?: number };
          if (payload.exp && payload.exp * 1000 > Date.now()) {
            return token;
          }
        } catch {
          // non-JWT token, skip
        }
      }
    }
  }

  // Fallback: Amplify session (works in normal browser context, not Playwright restore)
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (token) return token;
  } catch {
    // Amplify session not ready
  }

  return null;
}

export async function refreshSession(): Promise<RefreshResult> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const user = await getCurrentUser();
      if (!user) {
        return { success: false, tokens: null, error: new Error('No authenticated user') };
      }

      const session = await fetchAuthSession({ forceRefresh: true });
      const accessToken = session.tokens?.accessToken?.toString() ?? null;
      const idToken = session.tokens?.idToken?.toString() ?? null;
      
      const expiresAt = session.tokens?.accessToken?.payload?.exp
        ? session.tokens.accessToken.payload.exp * 1000
        : null;

      return {
        success: true,
        tokens: { accessToken, idToken, expiresAt },
        error: null,
      };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Session refresh failed');
      return { success: false, tokens: null, error };
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export function isTokenExpired(expiresAt: number | null, bufferMs: number = 5 * 60 * 1000): boolean {
  if (!expiresAt) return true;
  return Date.now() >= expiresAt - bufferMs;
}

export async function ensureValidToken(): Promise<string | null> {
  // Fast path: cached token still valid
  const cached = getCachedToken();
  if (cached) return cached;

  // Slow path: fetch from Amplify
  const session = await fetchAuthSession();
  const accessToken = session.tokens?.accessToken;
  
  if (!accessToken) {
    return null;
  }

  const exp = accessToken.payload.exp;
  const expiresAt = typeof exp === 'number' ? exp * 1000 : null;
  
  if (isTokenExpired(expiresAt)) {
    const refreshResult = await refreshSession();
    if (!refreshResult.success || !refreshResult.tokens?.accessToken) {
      return null;
    }
    const token = refreshResult.tokens.accessToken;
    const newExp = refreshResult.tokens.expiresAt;
    if (token && newExp) setCachedToken(token, newExp);
    return token;
  }

  // Cache the valid token
  const token = accessToken.toString();
  if (token && expiresAt) setCachedToken(token, expiresAt);
  return token;
}


